import { useRef, useState } from 'react';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { useEffect } from 'react';
import { apiMatch } from '../../api/apiMatch.api';
import { ProfileExampleByData } from '../../components/profile/ProfileExampleByData';
import { MeetActionButtons } from './MeetActionButtons';
import { toast } from 'react-toastify';
import DotSpinner from '../../components/shared/DotSpinner';
import { useFilters } from './hooks/useFilters';
import { LimitModal } from '../../components/limits/LimitModal';
import { checkLimitError } from '../../components/limits/utils/checkLimitError';
import { NotificationBySubscription } from '../subscription/NotificationBySubscription';
import { MatchScreen } from '../../components/MatchScreen';
import { logMatchEvent } from '../../utils/matchDebug';
import { apiUser } from '../../api/apiUser.api';
import GeolocationRequest from '../../components/GeolocationRequest';
import config from '../../config';
import { useSubscription } from '../../hooks/useSubscription';
import { DEFAULT_FILTERS, getActiveFiltersCount, saveFiltersToLocalStorage } from '../meet/utils/filters';
import { Button } from '../../components/Button';
import { useSelector } from 'react-redux';
import { AdBannerCard } from '../../components/ads/AdBannerCard';
import { DailyRewardModal } from '../../components/dailyReward/DailyRewardModal';
import { LikeBanModal } from '../../components/limits/LikeBanModal';
import { WALLET_UPDATED_EVENT } from '../../constants/events';
import { AnimatePresence, motion } from 'framer-motion';
import { HeartsBurst } from './HeartsBurst';
import { useNavigate } from 'react-router-dom';

// Направление, в котором анкета улетает при действии:
// дизлайк - влево с красной вуалью, лайк - вправо с зелёной, супер-лайк - вверх
const CARD_EXIT_EASE = [0.3, 0, 0.7, 0.5];

const cardVariants = {
  // Явные zIndex обязательны: покоящаяся карточка (1) всегда под улетающей (2).
  // Без них внутренние z-index'ы анкеты (фото z-[1], пагинация z-10) на части
  // устройств вылезают в общий контейнер и рисуются поверх уходящей карточки.
  initial: { opacity: 1, x: 0, y: 0, rotate: 0, scale: 1, zIndex: 1 },
  animate: { opacity: 1, x: 0, y: 0, rotate: 0, scale: 1, zIndex: 1 },
  exit: (direction) => {
    if (direction === 'left') {
      return {
        x: '-130%',
        rotate: -18,
        opacity: 0,
        zIndex: 2,
        pointerEvents: 'none',
        transition: { duration: 0.4, ease: CARD_EXIT_EASE }
      };
    }
    if (direction === 'right') {
      return {
        x: '130%',
        rotate: 18,
        opacity: 0,
        zIndex: 2,
        pointerEvents: 'none',
        transition: { duration: 0.4, ease: CARD_EXIT_EASE }
      };
    }
    if (direction === 'up') {
      return {
        y: '-120%',
        scale: 0.9,
        opacity: 0,
        zIndex: 2,
        pointerEvents: 'none',
        transition: { duration: 0.45, ease: CARD_EXIT_EASE }
      };
    }
    // прочие пути удаления анкеты (лимиты, 409 и т.п.) - быстрое затухание
    return { opacity: 0, zIndex: 2, pointerEvents: 'none', transition: { duration: 0.2 } };
  }
};

// цветовые вуали поверх уходящей анкеты
const redTintVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 0 },
  exit: (direction) => ({
    opacity: direction === 'left' ? 0.55 : 0,
    transition: { duration: 0.25 }
  })
};

const greenTintVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 0 },
  exit: (direction) => ({
    opacity: direction === 'right' || direction === 'up' ? 0.5 : 0,
    transition: { duration: 0.25 }
  })
};

const SUB_NOTIFY_DAILY_KEY = 'sub_notify_last_forced_date';
const DAILY_REWARD_SHOWN_KEY = 'daily_reward_last_shown_date';

// Плашка запроса геолокации: после закрытия не показываем сутки,
// иначе она всплывала после каждого лайка/дизлайка
const GEO_PROMPT_COOLDOWN_KEY = 'geo_prompt_dismissed_until';
const GEO_PROMPT_COOLDOWN_MS = 24 * 60 * 60 * 1000;

const isGeoPromptMuted = () => {
  const mutedUntil = Number(localStorage.getItem(GEO_PROMPT_COOLDOWN_KEY) || 0);
  return Date.now() < mutedUntil;
};

const getTodayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};

const minCount = 40;
const openChatNotifyMinCount = 25;
const countToShowADS = 10;

export function Meet() {
  const axiosPrivate = useAxiosPrivate();
  const { telegramId } = useSelector((state) => state.auth);
  const { isActiveSubscription } = useSubscription();
  const navigate = useNavigate();

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [showGeolocationRequest, setShowGeolocationRequest] = useState(false);

  const [subscriptionCount, setSubscriptionCount] = useState(0);
  const [openChatNotifyCount, setOpenChatNotifyCount] = useState(0);

  const [userData, setUserData] = useState(null);

  const [userMatches, setUsersMatches] = useState([]);
  const [lastUserMatches, setLastUserMatches] = useState(null);
  const [limitsKey, setLimitsKey] = useState(null);
  const [likeBanMs, setLikeBanMs] = useState(null);
  const [showMatchesScreen, setShowMatchesScreen] = useState(false);
  // Баннер ежедневной награды — раз в сутки при первом заходе
  const [showDailyReward, setShowDailyReward] = useState(false);

  const currentViewerUser = userMatches?.[0] ?? null;
  const isCurrentUserLikeMe = currentViewerUser?.likedMe;
  const [isLikeLastUserForMe, setIsLikeLastUserForMe] = useState(false);

  // Анимация ухода анкеты: направление вылета и всплеск сердечек при супер-лайке
  const [exitDirection, setExitDirection] = useState(null); // 'left' | 'right' | 'up'
  const [heartsKey, setHeartsKey] = useState(0);

  const { filters, setFilters } = useFilters();
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const loadingRef = useRef(null);

  useEffect(() => {
    if (!filters) return;

    const fetch = async () => {
      setIsLoading(true);
      await fetchPotentialUsers(1, true);
      await fetchLimitCheckUser();
      setIsLoading(false);
    };
    fetch();
  }, [filters]);

  // Баннер ежедневной награды: раз в сутки при первом заходе —
  // но только если пользователь уже ставил лайки (новичкам покажем при первом лайке)
  useEffect(() => {
    const todayKey = getTodayKey();
    if (sessionStorage.getItem(DAILY_REWARD_SHOWN_KEY) === todayKey) return;

    axiosPrivate
      .get(config.API.DAILY_REWARD.STATUS)
      .then((res) => {
        if (res.data?.data?.nextDay && res.data?.data?.hasLiked) {
          setShowDailyReward(true);
          sessionStorage.setItem(DAILY_REWARD_SHOWN_KEY, todayKey);
        }
      })
      .catch((error) => console.error('Ошибка проверки ежедневной награды:', error));
  }, []);

  // Первый лайк в приложении — показываем экран ежедневной награды.
  // deferUntilMatchClose: если этот же лайк оказался взаимным, откладываем
  // награду до закрытия экрана «Это взаимно», чтобы модалки не конфликтовали
  const [pendingDailyRewardAfterMatch, setPendingDailyRewardAfterMatch] = useState(false);

  const showDailyRewardAfterFirstLike = async (deferUntilMatchClose = false) => {
    const todayKey = getTodayKey();
    if (sessionStorage.getItem(DAILY_REWARD_SHOWN_KEY) === todayKey) return;

    try {
      const res = await axiosPrivate.get(config.API.DAILY_REWARD.SUMMARY);
      if (res.data?.data?.claimable) {
        sessionStorage.setItem(DAILY_REWARD_SHOWN_KEY, todayKey);

        if (deferUntilMatchClose) {
          setPendingDailyRewardAfterMatch(true);
        } else {
          setShowDailyReward(true);
        }
      }
    } catch (error) {
      console.error('Ошибка проверки ежедневной награды:', error);
    }
  };

  useEffect(() => {
    if (userMatches.length <= 1) {
      fetchPotentialUsers(page + 1);
    }
  }, [userMatches]);

  useEffect(() => {
    apiUser.fetchMe(axiosPrivate).then((res) => {
      setUserData(res.data);
    });
  }, []);

  useEffect(() => {
    if (isActiveSubscription !== false) return;

    const today = getTodayKey();
    const lastForcedDate = localStorage.getItem(SUB_NOTIFY_DAILY_KEY);

    if (lastForcedDate !== today) {
      localStorage.setItem(SUB_NOTIFY_DAILY_KEY, today);
      setSubscriptionCount(minCount);
    }
  }, [isActiveSubscription]);

  // Зеркало showMatchesScreen для синхронных проверок: state в момент
  // вызова checkCoordinates из finally лайка ещё не успевает обновиться
  // (stale closure), из-за чего гео-диалог (z-9999) перекрывал MatchScreen
  const showMatchesScreenRef = useRef(false);

  const checkCoordinates = () => {
    if (isGeoPromptMuted()) return;
    // Не перекрываем экран «Это взаимно» гео-диалогом (z-9999 выше MatchScreen)
    if (showMatchesScreenRef.current) return;

    const coordinate = userData?.location?.coordinates || [];
    if (coordinate.length < 2 || coordinate.every((c) => c === 0)) {
      setShowGeolocationRequest(true);
    }
  };

  const fetchPotentialUsers = async (nextPage = 1, replace = false) => {
    if (!filters || (!replace && nextPage > totalPage) || loadingRef.current) return;
    loadingRef.current = true;

    try {
      const response = await apiMatch.fetchPotential(axiosPrivate, { filters, page: nextPage });

      setUsersMatches((prev) => (replace ? response.users : [...prev, ...response.users]));
      setTotalPage(response.totalPages);
      setPage(nextPage);
    } catch (error) {
      if (error.response.data.message) {
        toast.error(error.response.data.message);
      }
    }

    loadingRef.current = false;
  };

  const fetchLimitCheckUser = async () => {
    try {
      await apiMatch.fetchLimitCheck(axiosPrivate);
    } catch (error) {
      if (error.response.data.message) {
        toast.error(error.response.data.message);
      }
    }
  };

  const deleteFirstUser = () => {
    setLastUserMatches(currentViewerUser);
    setUsersMatches((prev) => {
      return prev.filter((_, i) => i !== 0);
    });
  };

  const handleLikeOrDisLikeUser = async ({ targetUserId, like = true }) => {
    if (!targetUserId) return;

    setIsActionLoading(true);

    try {
      logMatchEvent('like_request', { target: targetUserId, like, via: 'like' });

      const data = await apiMatch.fetchLikeOrDisLike(axiosPrivate, {
        targetUserId,
        like
      });

      deleteFirstUser();

      // Экран «Это взаимно» показываем по авторитетному флагу isMutual из
      // ответа API (лайк мог быть взаимным, даже если анкета попала в ленту
      // без отметки likedMe — например, после круговорота дизлайков)
      const isMutualNow = like && (data?.match?.isMutual || isCurrentUserLikeMe);

      logMatchEvent('like_response', {
        target: targetUserId,
        isMutualInResponse: data?.match?.isMutual,
        firstReaction: data?.match?.firstReaction,
        cardLikedMe: isCurrentUserLikeMe,
        decision: isMutualNow ? 'SHOW_MATCH_SCREEN' : 'NO_MATCH_SCREEN'
      });

      if (like && data?.match?.firstReaction) {
        // Первый лайк совпал с взаимностью — DailyRewardModal не должен
        // перекрывать MatchScreen: показываем награду после его закрытия
        showDailyRewardAfterFirstLike(isMutualNow);
      }

      if (isMutualNow) {
        showMatchesScreenRef.current = true;
        setShowMatchesScreen(true);
        setLastUserMatches((prev) => ({
          ...prev,
          likedMe: false
        }));
      }

      setIsLikeLastUserForMe(like);
    } catch (error) {
      const message = error?.response?.data?.message;
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      // Ловим и код cliente-side падения (например, undefined-модуль в try) —
      // иначе такие ошибки молча глотаются и экран просто не показывается
      logMatchEvent('like_error', {
        target: targetUserId,
        status: status ?? null,
        code: code ?? null,
        message: message ?? String(error?.message ?? error)
      });

      const limitKey = checkLimitError(message);

      if (status === 429 && limitKey === 'likeBan') {
        setLikeBanMs(error?.response?.data?.meta?.remainingMs ?? 600_000);
        return;
      }

      if (status === 429 && limitKey) {
        setLimitsKey(limitKey);
      }

      if (status === 409 && code === 'ALREADY_LIKED') {
        deleteFirstUser();
      }
      if (!limitKey) {
        toast.error(message);
      }
    } finally {
      setIsActionLoading(false);
      checkCoordinates();
    }
  };

  const handleSuperLikeUser = async ({ targetUserId, pay = false }) => {
    if (!targetUserId) return false;

    setIsActionLoading(true);

    try {
      logMatchEvent('like_request', { target: targetUserId, like: true, via: 'superlike', pay });

      const data = await apiMatch.fetchSuperLike(axiosPrivate, {
        targetUserId,
        pay
      });

      if (data.match.isPaid) {
        window.dispatchEvent(new Event(WALLET_UPDATED_EVENT));
      }

      const isMutualNow = data?.match?.isMutual || isCurrentUserLikeMe;

      logMatchEvent('like_response', {
        target: targetUserId,
        via: 'superlike',
        isMutualInResponse: data?.match?.isMutual,
        firstReaction: data?.match?.firstReaction,
        cardLikedMe: isCurrentUserLikeMe,
        decision: isMutualNow ? 'SHOW_MATCH_SCREEN' : 'NO_MATCH_SCREEN'
      });

      if (data?.match?.firstReaction) {
        // Награда не перекрывает экран взаимности — покажем после его закрытия
        showDailyRewardAfterFirstLike(isMutualNow);
      }

      if (isMutualNow) {
        showMatchesScreenRef.current = true;
        setShowMatchesScreen(true);
        setLastUserMatches((prev) => ({
          ...prev,
          likedMe: false
        }));
      }

      setIsLikeLastUserForMe(true);
      // Всплеск сердечек — только на успешном супер-лайке, чтобы при исчерпании
      // лимита анимация не проигрывалась до оплаты в модалке
      setHeartsKey((key) => key + 1);
      deleteFirstUser();
      return true;
    } catch (error) {
      const message = error?.response?.data?.message;
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      if (status === 402) {
        toast.error('Недостаточно монет на балансе');
        return false;
      }

      const limitKey = checkLimitError(message);

      if (status === 429 && limitKey) {
        setLimitsKey(limitKey);
      }

      if (status === 409 && code === 'ALREADY_LIKED') {
        deleteFirstUser();
      }

      if (!limitKey) {
        toast.error(message);
      }
      return false;
    } finally {
      setIsActionLoading(false);
      checkCoordinates();
    }
  };

  const handleMatchReturn = async ({ pay = false } = {}) => {
    if (!lastUserMatches) return false;
    setIsActionLoading(true);

    try {
      const result = await apiMatch.fetchMatchReturn(axiosPrivate, {
        targetUserId: lastUserMatches._id,
        pay
      });
      if (result.isPaid) {
        window.dispatchEvent(new Event(WALLET_UPDATED_EVENT));
      }

      setUsersMatches((prev) => [lastUserMatches, ...prev]);
      setLastUserMatches(null);
      setIsLikeLastUserForMe(false);
      return true;
    } catch (error) {
      const message = error?.response?.data?.message;
      const status = error?.response?.status;

      if (status === 402) {
        toast.error('Недостаточно монет на балансе');
        return false;
      }

      const limitKey = checkLimitError(message);

      if (limitKey) {
        setLimitsKey(limitKey);
      } else {
        toast.error(message);
      }
      return false;
    } finally {
      setIsActionLoading(false);
    }
  };

  // Общий сценарий супер-лайка: направление вылета, счётчики и сам запрос.
  // Сердечки запускаются только при успехе (в handleSuperLikeUser),
  // pay: true — платный повтор действия из модалки лимитов
  const superLikeAction = ({ pay = false } = {}) => {
    if (!currentViewerUser) return false;

    setExitDirection('up');
    incrementSwipeCounters();
    decrementsADSCounter();
    return handleSuperLikeUser({ targetUserId: currentViewerUser._id, pay });
  };

  // Покупка действия за монеты из модалки лимитов: закрываем модалку только при успехе
  const handleLimitBuy = async () => {
    let ok = false;

    if (limitsKey === 'superlike') {
      ok = await superLikeAction({ pay: true });
    } else if (limitsKey === 'rewind') {
      ok = await handleMatchReturn({ pay: true });
    }

    if (ok) setLimitsKey(null);
    if (!ok) navigate(config.ROUTES.PAYMENT);
  };

  const incrementSwipeCounters = () => {
    setSubscriptionCount((prev) => prev + 1);
    setOpenChatNotifyCount((prev) => prev + 1);
  };

  const [isBannerAd, setIsBannerAd] = useState(false);

  const decrementsADSCounter = () => {
    // У премиум-подписки рекламы нет — не считаем свайпы и не показываем баннер
    if (isActiveSubscription) return;

    const count = localStorage.getItem('adsCount') || 0;
    const newCount = parseInt(count) + 1;
    localStorage.setItem('adsCount', newCount);

    if (newCount >= countToShowADS) {
      // Баннер RichAds на месте анкеты: кнопки лайков скрываются,
      // закрытие — крестиком после таймера
      setIsBannerAd(true);
      localStorage.setItem('adsCount', 0);
    }
  };

  const handleGeolocationComplete = async (coordinates) => {
    try {
      await axiosPrivate.put(config.API.USERS.UPDATE_LOCATION, {
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        accuracy: coordinates.accuracy
      });
      await apiUser.fetchMe(axiosPrivate).then((res) => {
        setUserData(res.data);
      });
      localStorage.removeItem(GEO_PROMPT_COOLDOWN_KEY);
    } catch (error) {
      console.error('Ошибка сохранения геолокации:', error);
      toast.error('Ошибка сохранения геолокации');
      // Координаты не сохранились — сутки не дёргаем пользователя повторным запросом
      localStorage.setItem(GEO_PROMPT_COOLDOWN_KEY, String(Date.now() + GEO_PROMPT_COOLDOWN_MS));
    }
    setShowGeolocationRequest(false);
  };

  // Закрытие плашки крестиком или «Позже»: сутки не напоминаем
  const handleSkipGeolocation = () => {
    localStorage.setItem(GEO_PROMPT_COOLDOWN_KEY, String(Date.now() + GEO_PROMPT_COOLDOWN_MS));
    setShowGeolocationRequest(false);
  };

  const isNoMoreFavorite = !currentViewerUser && !isLoading;

  // Сброс фильтров прямо со страницы знакомств: чистим localStorage и профиль,
  // обновление состояния filters запускает повторную загрузку анкет
  const handleResetFilters = async () => {
    setIsSaving(true);
    saveFiltersToLocalStorage(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);

    try {
      await axiosPrivate.put(config.API.USERS.UPDATE_FILTERS, {
        telegramId,
        filters: DEFAULT_FILTERS
      });
    } catch (error) {
      console.error('Error resetting filters:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Сколько фото анкеты можно смотреть: сколько загружено у себя (минимум 1 — первое видно всем).
  // Premium — без ограничений.
  const photosVisibleCount = isActiveSubscription ? Infinity : Math.max(1, userData?.photos?.length || 0);

  return (
    <div
      className={`relative rounded-2xl mx-4 h-[calc(100%-178px)] ${subscriptionCount >= minCount ? 'overflow-hidden' : 'overflow-y-auto'}`}
    >
      {showGeolocationRequest && (
        <GeolocationRequest onComplete={handleGeolocationComplete} onSkip={handleSkipGeolocation} />
      )}
      {showMatchesScreen && lastUserMatches && (
        <MatchScreen
          recipientId={lastUserMatches._id}
          userAPhotoUrl={userData?.photos[0]}
          userBPhotoUrl={lastUserMatches.photos[0]}
          close={() => {
            showMatchesScreenRef.current = false;
            setShowMatchesScreen(false);
            logMatchEvent('match_screen_closed', {
              deferredRewardShown: pendingDailyRewardAfterMatch
            });
            // Отложенная награда за первый лайк — теперь её очередь
            if (pendingDailyRewardAfterMatch) {
              setPendingDailyRewardAfterMatch(false);
              setShowDailyReward(true);
            }
          }}
        />
      )}
      {!isActiveSubscription && (
        <NotificationBySubscription
          count={subscriptionCount}
          resetCount={() => setSubscriptionCount(0)}
          minCount={minCount}
        />
      )}
      {isLoading && (
        <div className="flex items-center justify-center min-h-[75vh]">
          <DotSpinner />
        </div>
      )}
      {!isNoMoreFavorite && !isLoading && (
        <div className="flex flex-col h-full">
          <div className="relative flex-1 min-h-0">
            <AnimatePresence custom={exitDirection} initial={false}>
              {currentViewerUser && (
                <motion.div
                  key={currentViewerUser._id}
                  className="absolute inset-0"
                  variants={cardVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  custom={exitDirection}
                >
                  <ProfileExampleByData
                    className="px-0 py-0 select-none h-full overflow-y-auto"
                    userData={currentViewerUser}
                    showActionButtons={false}
                    photosVisibleCount={photosVisibleCount}
                  />
                  <motion.div
                    variants={redTintVariants}
                    custom={exitDirection}
                    className="absolute inset-0 z-40 pointer-events-none bg-gradient-to-br from-[#CE5D49]/80 to-[#CE5D49]/30"
                  />
                  <motion.div
                    variants={greenTintVariants}
                    custom={exitDirection}
                    className="absolute inset-0 z-40 pointer-events-none bg-gradient-to-br from-[#A1F69E]/70 to-[#A1F69E]/20"
                  />
                </motion.div>
              )}
            </AnimatePresence>
            {isBannerAd && !isActiveSubscription && <AdBannerCard onClose={() => setIsBannerAd(false)} />}
            <HeartsBurst triggerKey={heartsKey} />
          </div>
          {/* Пока показывается реклама — кнопки лайков скрыты, баннер занимает всё место */}
          {!isNoMoreFavorite && !isLoading && currentViewerUser && !(isBannerAd && !isActiveSubscription) && (
            <MeetActionButtons
              likedMe={lastUserMatches?.likedMe && !isLikeLastUserForMe}
              backCardDisabled={!lastUserMatches}
              openChatNotifyCount={openChatNotifyCount}
              openChatNotifyMinCount={openChatNotifyMinCount}
              backCard={() => handleMatchReturn()}
              skipCard={() => {
                setExitDirection('left');
                handleLikeOrDisLikeUser({ targetUserId: currentViewerUser._id, like: false });
                incrementSwipeCounters();
                decrementsADSCounter();
              }}
              likeCard={() => {
                setExitDirection('right');
                handleLikeOrDisLikeUser({ targetUserId: currentViewerUser._id, like: true });
                incrementSwipeCounters();
                decrementsADSCounter();
              }}
              superLikeCard={() => superLikeAction()}
              goToChatCard={() => currentViewerUser?._id}
              disabledButton={isActionLoading}
            />
          )}
        </div>
      )}

      {isNoMoreFavorite && !isLoading && getActiveFiltersCount() === 0 && (
        <div className="flex flex-col items-center justify-center h-full bg-[#043939] px-4">
          <img src="/icons/meet-not-found.svg" alt="meet-not-found" className="w-[100px]" />
          <p className="text-center font-semibold text-[18px] text-white mt-3">
            На сегодня анкеты закончились... <br /> Отдохните немного и приходите завтра!
          </p>
        </div>
      )}
      {isNoMoreFavorite && !isLoading && getActiveFiltersCount() > 0 && (
        <div className="flex flex-col items-center justify-center h-full bg-[#043939] px-4">
          <img src="/icons/meet-oops.svg" alt="meet-oops" className="w-[100px]" />
          <p className="text-center font-semibold text-[18px] text-white mt-3">
            Ой, по этому запросу пока ничего не нашлось
          </p>
          <Button onClick={handleResetFilters} disabled={isSaving} loading={isSaving} className="w-max mt-6">
            Сбросить фильтр
          </Button>
        </div>
      )}

      {limitsKey && (
        <LimitModal domain="swipe" limitsKey={limitsKey} setLimitsKey={setLimitsKey} onBuy={handleLimitBuy} />
      )}
      {likeBanMs !== null && <LikeBanModal remainingMs={likeBanMs} onClose={() => setLikeBanMs(null)} />}

      <DailyRewardModal isOpen={showDailyReward} onClose={() => setShowDailyReward(false)} />
    </div>
  );
}
